import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaGift, FaUsers, FaStar, FaCopy, FaEnvelope, FaCheck, FaTimes, FaClock, FaShoppingCart, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTheme } from '../../context/SocketContext';

const ReferAndEarn = () => {
  const { currentThemeColors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/referrals/my-referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      } else {
        toast.error('Failed to load referral data');
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (referralData?.referralLink) {
      navigator.clipboard.writeText(referralData.referralLink);
      setCopied(true);
      toast.success('Referral link copied to clipboard!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleCopyCode = () => {
    if (referralData?.referralCode) {
      navigator.clipboard.writeText(referralData.referralCode);
      toast.success('Referral code copied to clipboard!');
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteForm.email) {
      toast.error('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setInviteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/referrals/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          referredEmail: inviteForm.email,
          referredName: inviteForm.name
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Invitation sent successfully!');
        setShowInviteModal(false);
        setInviteForm({ email: '', name: '' });
        fetchReferralData(); // Refresh data
      } else {
        toast.error(data.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error('Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FaClock className="w-4 h-4 text-yellow-500" />;
      case 'registered':
        return <FaCheck className="w-4 h-4 text-blue-500" />;
      case 'purchased':
        return <FaShoppingCart className="w-4 h-4 text-green-500" />;
      case 'rewarded':
        return <FaStar className="w-4 h-4 text-primary-500" />;
      default:
        return <FaClock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Invitation Sent';
      case 'registered':
        return 'Signed Up';
      case 'purchased':
        return 'Made Purchase';
      case 'rewarded':
        return 'Reward Earned';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FaSpinner className="w-12 h-12 text-primary-500 animate-spin mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading referral data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative min-h-[60vh]">
      {/* Page Header */}
      <div className="flex items-center gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}>
        <div 
          className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-110 hover:rotate-6"
          style={{ 
            background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
            boxShadow: `0 10px 15px -3px var(--glow-color)`
          }}
        >
          <FaGift className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-dark-800 dark:text-white">Refer & Earn</h1>
          <p className="text-gray-500 dark:text-gray-400">Invite friends and earn 1000 reward points for each successful referral!</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="card p-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <FaUsers className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-800 dark:text-white">{referralData?.stats?.totalReferrals || 0}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Referrals</p>
            </div>
          </div>
        </div>

        <div className="card p-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <FaClock className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-800 dark:text-white">{referralData?.stats?.pendingReferrals || 0}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
            </div>
          </div>
        </div>

        <div className="card p-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <FaCheck className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-800 dark:text-white">{referralData?.stats?.purchasedReferrals || 0}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Successful</p>
            </div>
          </div>
        </div>

        <div 
          className="card p-4 border-0 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
          style={{ background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))` }}
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <FaStar className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{referralData?.stats?.availablePoints || 0}</p>
              <p className="text-sm text-white/80">Reward Points</p>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Link Section */}
      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
        <h2 className="text-lg font-semibold text-dark-800 dark:text-white mb-2">Your Referral Link</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Share this link with friends to earn rewards when they sign up and make a purchase.</p>
        
        <div className="space-y-3">
          <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
            <span className="flex-1 text-sm text-dark-800 dark:text-gray-300 truncate font-mono">{referralData?.referralLink || 'Loading...'}</span>
            <button 
              onClick={handleCopyLink}
              className={`p-2 rounded-lg transition-colors ${copied ? 'bg-green-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'}`}
            >
              {copied ? <FaCheck className="w-4 h-4" /> : <FaCopy className="w-4 h-4" />}
            </button>
          </div>
          
          <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
            <span className="text-sm text-gray-600 dark:text-gray-400">Your Code: <strong className="text-primary-600 dark:text-primary-400 font-mono">{referralData?.referralCode || '-'}</strong></span>
            <button onClick={handleCopyCode} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-gray-200 dark:bg-dark-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-dark-500 transition-colors">
              <FaCopy className="w-3 h-3" /> Copy
            </button>
          </div>
        </div>

        <button onClick={() => setShowInviteModal(true)} className="mt-4 flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium hover:shadow-lg hover:shadow-primary-500/30 transition-all">
          <FaEnvelope className="w-4 h-4" /> Send Invitation Email
        </button>
      </div>

      {/* How It Works Section */}
      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
        <h2 className="text-lg font-semibold text-dark-800 dark:text-white mb-4">How It Works</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { num: 1, title: 'Share Your Link', desc: 'Share your unique referral link or code with friends and colleagues.' },
            { num: 2, title: 'Friend Signs Up', desc: 'Your friend creates an account using your referral link.' },
            { num: 3, title: 'They Make a Purchase', desc: 'When they purchase any service, you earn your reward!' },
            { num: 4, title: 'Earn 1000 Points', desc: 'Get 1000 reward points to use towards your next purchase.' }
          ].map((step) => (
            <div key={step.num} className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 text-center">
              <div className="w-10 h-10 rounded-full bg-primary-500 text-white font-bold flex items-center justify-center mx-auto mb-3">{step.num}</div>
              <h3 className="font-semibold text-dark-800 dark:text-white mb-1">{step.title}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* My Referrals Table */}
      <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
          <h2 className="text-lg font-semibold text-dark-800 dark:text-white">My Referrals</h2>
        </div>
        {referralData?.referrals?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-dark-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Invited On</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Registered</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Reward</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                {referralData.referrals.map((referral) => (
                  <tr key={referral.id} className="hover:bg-gray-50 dark:hover:bg-dark-700/50">
                    <td className="px-4 py-3 text-sm text-dark-800 dark:text-gray-300">{referral.referredEmail}</td>
                    <td className="px-4 py-3 text-sm text-dark-800 dark:text-gray-300">{referral.referredName || referral.referredUser?.name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        referral.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                        referral.status === 'registered' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                        referral.status === 'purchased' || referral.status === 'rewarded' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                        'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}>
                        {getStatusIcon(referral.status)}
                        {getStatusLabel(referral.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{formatDate(referral.invitationSentAt)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{formatDate(referral.registeredAt)}</td>
                    <td className="px-4 py-3">
                      {referral.status === 'rewarded' || referral.status === 'purchased' ? (
                        <span className="font-semibold text-green-600 dark:text-green-400">{referral.rewardAmount} pts</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <FaUsers className="w-12 h-12 text-gray-300 dark:text-dark-500 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No referrals yet. Start inviting friends to earn rewards!</p>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && createPortal(
        <>
          <div className="fixed inset-0 z-[60] bg-blue-900/30 backdrop-blur-sm pointer-events-none" />
          <div className="fixed inset-0 z-[70]" onClick={() => setShowInviteModal(false)} />
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <FaEnvelope className="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-dark-800 dark:text-white">Send Invitation</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Enter your friend's details</p>
                  </div>
                </div>
                <button onClick={() => setShowInviteModal(false)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors">
                  <FaTimes className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleInviteSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">Email Address *</label>
                  <input
                    type="email"
                    placeholder="friend@example.com"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    required
                    className="w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">Friend's Name (Optional)</label>
                  <input
                    type="text"
                    placeholder="John Doe"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    className="w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 px-4 py-3 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 font-medium hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviteLoading}
                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium hover:shadow-lg disabled:opacity-50 transition-all"
                  >
                    {inviteLoading ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default ReferAndEarn;
